/**
 * Country Context
 *
 * React Context for managing global country state based on subdomain detection.
 * Provides country information to all components in the application.
 */

import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import {
    getCountryFromSubdomain,
    isGlobalPath,
    getCountryConfig,
    validateCountry
} from '../utils/countryUtils';
import { COUNTRY_CONFIG } from '../config/countryConfig';

const CountryContext = createContext();

/**
 * Hook to access country context
 * @returns {object} Country context value
 */
export const useCountry = () => {
    const context = useContext(CountryContext);
    if (!context) {
        throw new Error('useCountry must be used within a CountryProvider');
    }
    return context;
};

/**
 * Country Provider Component
 * Detects country from subdomain and provides it to the app
 */
export const CountryProvider = ({ children }) => {
    const [country, setCountry] = useState(null);
    const [isGlobal, setIsGlobal] = useState(false);
    const [isInvalid, setIsInvalid] = useState(false);
    const [loading, setLoading] = useState(true);

    // Function to detect and update country from subdomain
    const detectCountry = () => {
        const hostname = window.location.hostname;
        const detectedCountry = getCountryFromSubdomain();
        const globalDomain = isGlobalPath();

        if (globalDomain && !detectedCountry) {
            // Global domain - no country specified
            setIsGlobal(true);
            setCountry(null);
            setIsInvalid(false);
        } else if (detectedCountry === 'INVALID') {
            // Invalid country subdomain
            setIsInvalid(true);
            setCountry(null);
            setIsGlobal(false);
        } else if (detectedCountry && validateCountry(detectedCountry)) {
            // Valid country detected
            setCountry(detectedCountry);
            setIsGlobal(false);
            setIsInvalid(false);
        } else {
            // Fallback to global
            setIsGlobal(true);
            setCountry(null);
            setIsInvalid(false);
        }

        setLoading(false);
    };

    // Detect country from subdomain on mount
    // Note: We don't need to re-detect on route changes since subdomain doesn't change
    useEffect(() => {
        detectCountry();
    }, []);

    // Get country configuration
    const countryConfig = useMemo(() => {
        if (!country) return null;
        return getCountryConfig(country);
    }, [country]);

    // Get all countries for selector
    const allCountries = useMemo(() => {
        return Object.entries(COUNTRY_CONFIG).map(([key, config]) => ({
            key,
            ...config
        }));
    }, []);

    const value = {
        // Current country state
        country,              // Country code (e.g., 'PE') or null
        countryConfig,        // Full country configuration object
        isGlobal,            // True if no country in path
        isInvalid,           // True if invalid country slug
        loading,             // True while detecting country

        // All countries for selector
        allCountries,

        // Manual country setter (for testing or manual override)
        setCountry: (newCountry) => {
            if (validateCountry(newCountry)) {
                setCountry(newCountry);
                setIsGlobal(false);
                setIsInvalid(false);
            }
        }
    };

    return (
        <CountryContext.Provider value={value}>
            {children}
        </CountryContext.Provider>
    );
};
